import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "../api/api";
import { useAuth } from "../auth/AuthProvider";

const DEFAULT_LOW_STOCK_THRESHOLD = 5;

const AppSettingsContext = createContext({
  settings: { lowStockThreshold: DEFAULT_LOW_STOCK_THRESHOLD },
  loading: false,
  error: null,
  refresh: async () => {},
  setLowStockThresholdLocal: () => {},
});

export function AppSettingsProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [settings, setSettings] = useState({
    lowStockThreshold: DEFAULT_LOW_STOCK_THRESHOLD,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) return;

    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/settings");
      const n = Math.trunc(Number(res?.data?.lowStockThreshold));
      setSettings({
        lowStockThreshold:
          Number.isFinite(n) && n > 0 ? n : DEFAULT_LOW_STOCK_THRESHOLD,
      });
    } catch (e) {
      setError(e);
      setSettings({ lowStockThreshold: DEFAULT_LOW_STOCK_THRESHOLD });
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      setSettings({ lowStockThreshold: DEFAULT_LOW_STOCK_THRESHOLD });
      setLoading(false);
      setError(null);
      return;
    }
    refresh();
  }, [isAuthenticated, refresh]);

  const setLowStockThresholdLocal = useCallback((value) => {
    const n = Math.trunc(Number(value));
    setSettings({
      lowStockThreshold:
        Number.isFinite(n) && n > 0 ? n : DEFAULT_LOW_STOCK_THRESHOLD,
    });
  }, []);

  const ctx = useMemo(
    () => ({
      settings,
      loading,
      error,
      refresh,
      setLowStockThresholdLocal,
    }),
    [settings, loading, error, refresh, setLowStockThresholdLocal]
  );

  return <AppSettingsContext.Provider value={ctx}>{children}</AppSettingsContext.Provider>;
}

export function useAppSettings() {
  return useContext(AppSettingsContext);
}
